import * as Pixi from "pixi.js";

import { LayerName, LAYERS } from "../../config/layers";
import { GameSprite } from "../entity/GameSprite";
import { V, V2d } from "../Vector";
import { Camera2d } from "./Camera2d";
import { LayerInfo } from "./LayerInfo";

/** Options for the GameRenderer2d constructor */
export interface GameRenderer2dOptions extends Partial<Pixi.RendererOptions> {}

/**
 * The core 2D rendering system that manages the visual display pipeline.
 *
 * ## Architecture Overview
 *
 * GameRenderer2d acts as the bridge between the game engine and Pixi.js, providing:
 * - **Layer Management**: Organizes sprites into logical rendering layers with different properties
 * - **Camera Integration**: Applies camera transformations to create viewport effects
 * - **Sprite Lifecycle**: Manages adding/removing sprites from the rendering pipeline
 * - **Visual Effects**: Supports filters and post-processing effects per layer or globally
 *
 * ## Rendering Pipeline
 *
 * The rendering process follows this sequence each frame:
 * 1. **Layer Updates**: Camera applies transformations to each layer (position, scale, parallax)
 * 2. **Pixi Rendering**: Pixi.js renders all layers in z-order (defined by LAYERS object)
 * 3. **Post-Processing**: Any filters applied to layers or the entire stage
 * 4. **Display**: Final frame presented to the canvas
 *
 * ## Layer System
 *
 * Layers provide depth sorting and visual effects organization:
 * - Each layer is a Pixi Container with its own transformation matrix
 * - Layers render in the order defined in `LAYERS` (src/config/layers.ts)
 * - Different layers can have different parallax effects (e.g., background vs HUD)
 * - Filters can be applied per-layer for localized effects
 *
 * ## Integration with Pixi.js
 *
 * - **app**: The main Pixi Application that manages the WebGL/Canvas context
 * - **stage**: Root container that holds all layer containers
 * - **canvas**: HTML5 Canvas element where rendering output appears
 * - Uses Pixi's automatic density scaling for high-DPI displays
 *
 * ## Performance Considerations
 *
 * - Sprite count is tracked for debugging performance bottlenecks
 * - Layers allow for efficient culling and batching of similar objects
 * - Camera transformations are applied once per layer, not per sprite
 * - Automatic resize handling prevents layout thrashing
 *
 * @example
 * ```typescript
 * const renderer = new GameRenderer2d(LAYERS, "main");
 * await renderer.init({ antialias: true });
 *
 * // Add a sprite to the main layer
 * const sprite = loadGameSprite("hero.png", "main");
 * renderer.addSprite(sprite);
 *
 * // Apply a filter to the HUD layer
 * const blurFilter = new BlurFilter();
 * renderer.addLayerFilter(blurFilter, "hud");
 * ```
 */
export class GameRenderer2d {
  // TODO: Do we really need to store this ourselves? Can't we just set it on the canvas?
  private cursor: CSSStyleDeclaration["cursor"] = "none";

  /** The number of sprites currently managed by this renderer. Mostly useful for debugging. */
  public get spriteCount() {
    return this._spriteCount;
  }
  private set spriteCount(value) {
    this._spriteCount = value;
  }
  private _spriteCount: number = 0;

  /**
   * The Pixi.js application instance that manages the WebGL/Canvas rendering context.
   * Handles the main render loop, canvas management, and device pixel ratio scaling.
   */
  app: Pixi.Application;

  /**
   * The camera that controls viewport transformations and coordinate space conversions.
   * Automatically updates all layer transformations during the render cycle.
   */
  camera: Camera2d;

  /**
   * The root Pixi container that holds all rendering layers.
   * Each layer in LAYERS is added as a child of this stage container.
   */
  get stage(): Pixi.Container {
    return this.app.stage;
  }

  /**
   * The HTML5 Canvas element where all rendering output is displayed.
   * Managed by Pixi.js with automatic resizing and density scaling.
   */
  get canvas(): HTMLCanvasElement {
    return this.app.renderer.canvas;
  }

  private resizeHandler = () => this.handleResize();

  /**
   * Creates a new GameRenderer2d instance.
   *
   * @param layerInfos - Configuration for all rendering layers (from LAYERS config)
   * @param defaultLayerName - Layer to use for sprites that don't specify a layer
   * @param onResize - Optional callback fired when the renderer viewport changes size
   */
  constructor(
    private layerInfos: Record<LayerName, LayerInfo>,
    private defaultLayerName: LayerName,
    private onResize?: ([width, height]: [number, number]) => void,
  ) {
    this.app = new Pixi.Application();
    this.showCursor();
    this.camera = new Camera2d(this, V(0, 0));

    for (const layerInfo of Object.values(LAYERS)) {
      this.app.stage.addChild(layerInfo.container);
    }

    window.addEventListener("resize", this.resizeHandler);
  }

  async init(pixiOptions: GameRenderer2dOptions = {}) {
    await this.app
      .init({
        resizeTo: window,
        autoDensity: true,
        antialias: true,
        ...pixiOptions,
      })
      .then(() => {
        document.body.appendChild(this.canvas);
      });
  }

  /**
   * Requests fullscreen mode for the game canvas.
   * Waits for user interaction (click) to comply with browser security requirements.
   */
  requestFullscreen() {
    const makeFullScreen = () => {
      this.canvas.requestFullscreen();
      this.canvas.removeEventListener("click", makeFullScreen);
    };
    this.canvas.addEventListener("click", makeFullScreen);
  }

  /**
   * Gets the effective height of the renderer viewport in logical pixels.
   */
  getHeight(): number {
    return this.app.renderer.height / this.app.renderer.resolution;
  }

  /**
   * Gets the effective width of the renderer viewport in logical pixels.
   */
  getWidth(): number {
    return this.app.renderer.width / this.app.renderer.resolution;
  }

  getSize(): V2d {
    return V(this.getWidth(), this.getHeight());
  }

  handleResize() {
    this.app.resizeTo = window;
    this.app.resize();
    this.onResize?.(this.getSize());
  }

  hideCursor() {
    this.cursor = "none";
  }

  showCursor() {
    this.cursor = "auto";
  }

  setCursor(value: CSSStyleDeclaration["cursor"]) {
    this.cursor = value;
  }

  /**
   * Renders the current frame to the canvas.
   *
   * This is the core of the rendering pipeline:
   * 1. Updates all layer transformations via camera.updateLayer()
   * 2. Calls Pixi's render() to draw everything to the canvas
   * 3. Updates the cursor style if needed
   *
   * Called once per frame by the main game loop.
   */
  render() {
    for (const layerInfo of Object.values(this.layerInfos)) {
      this.camera.updateLayer(layerInfo);
    }
    this.app.render();
    if (this.app.renderer.view.canvas.style) {
      this.app.renderer.view.canvas.style.cursor = this.cursor;
    }
  }

  addSprite(sprite: GameSprite): GameSprite {
    const layerName = sprite.layerName ?? this.defaultLayerName;
    this.layerInfos[layerName].container.addChild(sprite);
    this.spriteCount += 1;
    return sprite;
  }

  /**
   * Removes a sprite from its designated layer.
   *
   * @param sprite - The GameSprite to remove from rendering
   */
  removeSprite(sprite: GameSprite): void {
    const layerName = sprite.layerName ?? this.defaultLayerName;
    this.layerInfos[layerName].container.removeChild(sprite);
    this.spriteCount -= 1;
  }

  /**
   * Adds a visual filter effect to a specific rendering layer.
   * @param filter - Pixi filter to apply to the layer
   * @param layerName - Name of the layer to apply the filter to
   */
  addLayerFilter(filter: Pixi.Filter, layerName: LayerName): void {
    const container = this.layerInfos[layerName].container;
    if (!(container.filters instanceof Array)) {
      throw new Error("layer.filters is not an array");
    }
    container.filters;
    container.filters = [...container.filters!, filter];
  }

  addStageFilter(filter: Pixi.Filter): void {
    if (!this.stage.filters) {
      this.stage.filters = [];
    }
    this.stage.filters = [...this.stage.filters, filter];
  }

  removeStageFilter(filterToRemove: Pixi.Filter): void {
    if (!this.stage.filters) {
      return;
    }
    this.stage.filters = this.stage.filters.filter(
      (filter) => filter !== filterToRemove,
    );
  }

  /**
   * Cleans up resources when the renderer is no longer needed.
   * Should be called when destroying the game instance.
   */
  destroy(): void {
    window.removeEventListener("resize", this.resizeHandler);
    this.app.destroy();
  }
}
